/**
 * Copy a campaign group to another country (19/10/2026): its master config
 * (rules) and its containers, pasted into a campaign group of another
 * country's plan (or another group of the same country). What only exists in
 * the source country — gateways, lists, campaigns — finds its twin in the
 * target (v2, 26/10/2026: a gateway by its SMSWiser gateway id, the same in
 * every country; a list or campaign by its name without the country prefix;
 * a match a person picked before between the two countries) or is chosen in
 * the review; whatever still can't come over is listed
 * in the review before Paste, never dropped silently. Pasted containers
 * arrive switched off and auto-send arrives off.
 * The server answers the review (POST …/review) and applies it (POST …/paste)
 * from the same request, so what the review shows is what Paste does.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenSection } from "../master/master.types";

/** A campaign group of one country's plan. */
export interface GroupCopyRef {
  countryKey: string;
  /** The campaign group key (DropGenCampaignGroup.key). */
  groupKey: string;
}

/** A list group the target country lacks, re-created there with the same name and rule. */
export const GROUP_COPY_CREATE = "create" as const;

/** The person's decisions in the review; every field optional — absent = the automatic answer. */
export interface GroupCopyChoices {
  /**
   * Master config sections taken from the copy when the target group already
   * has a master config; the others keep the target's values. Absent = every
   * section. Ignored when the target has none (it gets every section).
   */
  sections?: DropGenSection[];
  /** Source container ids to bring (route test included); absent = all of them. */
  containerIds?: string[];
  /** Source gateway routeId (as a string key) → target routeId; null = leave its lanes and pins out. */
  gateways?: Record<string, number | null>;
  /** Source list group id → target list group id, GROUP_COPY_CREATE, or null (leave it out). */
  listGroups?: Record<string, string | null>;
  /** Source campaign wiserId → a campaign wiserId of the target group. */
  campaigns?: Record<string, number>;
  /** 26/10/2026 — source list wiserId → a target list wiserId; null = leave it out. */
  lists?: Record<string, number | null>;
  /** 26/10/2026 — source gateway routeIds whose lanes arrive without their sender IDs (kept by default). */
  clearSenders?: number[];
}

export interface GroupCopyRequest {
  source: GroupCopyRef;
  target: GroupCopyRef;
  choices?: GroupCopyChoices;
}
